import { Icons, SecondaryButton } from "@stacc/flow-ui-components";
import { type JSX, useState } from "react";
import { ApplicantInfo } from "../../../components/Applicant/ApplicantInfo";
import { callApplicant } from "../../../services/itx.service";
import { communicationTemplatesFI } from "../../../templates/finland/loan";
import { replaceVariablesInTemplateSE } from "../../../templates/templateHelpers";
import { type CommonData, Country } from "../../../types/common";
import type { TaskProps } from "../../../types/TaskProps";
import { isStringNullOrEmpty } from "../../../utils/helperFunctions";
import CommonUserTask from "../CommonUserTask";
import { DecisionSectionWrapper } from "../styles";
import { useTaskPermissions } from "../TaskPermissionsContext";
import {
  renderMessage,
  UsertaskButtons,
  UsertaskSelect,
  UsertaskTextArea
} from "../UsertaskComponents";
import { GapContainer } from "../update-application/style";
import {
  CallSectionWrapper,
  IconWrapper,
  List,
  ListItem,
  SMSConversationWrapper,
  StyledDecisionSectionWrapper,
  StyledSpan
} from "./styles";

type SmsOrMail = "" | "sms" | "mail";

interface CallError {
  error: unknown;
  message: string;
}

const ContactApplicantFinland = ({
  flow
}: {
  flow: TaskProps;
}): JSX.Element => {
  const { canAct } = useTaskPermissions();
  const {
    smsTo,
    template: templateData,
    message: messageData,
    mailAndOrSms: mailAndOrSmsData
  } = flow?.task?.data ?? {};
  const { agentApiApplication, offer, integrations, communication } =
    (flow?.flow?.data as CommonData) ?? {};
  const { applicant, coapplicant } = agentApiApplication ?? {};
  const emailTemplates = communicationTemplatesFI;
  //Fields for sms and mail
  const [to, setTo] = useState<string>(smsTo ?? "");
  const [template, setTemplate] = useState<string>(templateData ?? "");
  const [message, setMessage] = useState<string>(messageData ?? "");
  const [error, setError] = useState<CallError | null>(null);
  const [smsMailChoosen, setSmsMailChoosen] = useState<SmsOrMail>("");

  //Checkbox for sms and mail
  const [mailAndOrSms] = useState<string[]>(mailAndOrSmsData ?? []);

  //Dropdown for call
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  //Data variables
  const data = flow.flow?.data;

  //Get applicants and merge them to one array
  const applicants = [applicant];
  if (coapplicant !== undefined) {
    applicants.push(coapplicant);
  }

  const smsMailOptions = [
    { label: "Choose SMS or Mail...", value: "" },
    { label: "SMS", value: "sms" },
    { label: "Mail", value: "mail" }
  ];

  const smsToOptions = [
    { label: "Choose a recipient", value: "" },
    ...(applicant?.mobileNumber
      ? [
          {
            label: `${applicant?.firstName} ${applicant?.lastName}`,
            value: "applicant"
          }
        ]
      : []),
    ...(coapplicant?.mobileNumber
      ? [
          {
            label: `${coapplicant?.firstName} ${coapplicant?.lastName}`,
            value: "coapplicant"
          }
        ]
      : []),
    ...(applicant.mobileNumber && coapplicant?.mobileNumber
      ? [{ label: "Both", value: "both" }]
      : [])
  ];

  const emailToOptions = [
    { label: "Choose a recipient", value: "" },
    ...(applicant?.emailAddress
      ? [
          {
            label: `${applicant?.firstName} ${applicant?.lastName}`,
            value: "applicant"
          }
        ]
      : []),
    ...(coapplicant?.emailAddress
      ? [
          {
            label: `${coapplicant?.firstName} ${coapplicant?.lastName}`,
            value: "coapplicant"
          }
        ]
      : []),
    ...(applicant.emailAddress && coapplicant?.emailAddress
      ? [{ label: "Both", value: "both" }]
      : [])
  ];

  const templateOptions = Object.keys(emailTemplates)
    .filter((key) => emailTemplates[key].templateType === smsMailChoosen)
    .map((key) => ({
      label: emailTemplates[key].templateName,
      value: key,
      disabled: emailTemplates[key].variables.includes("signingLink") && !offer
    }));

  templateOptions.unshift({
    label: "No template / Free text",
    value: "",
    disabled: false
  });

  //Get mails with key string mailCommunication from communication
  const smsSent = integrations?.itx?.smsSent ?? [];
  const callsMade = integrations?.itx?.calls ?? [];
  const mailSent =
    communication?.filter(
      (mail: { key: string }) => mail.key === "mailCommunication"
    ) ?? [];

  //Merge sms, calls and mail
  const smsAndMailSent = [...smsSent, ...callsMade, ...mailSent].sort(
    (a, b) =>
      new Date(Date.parse(a.timestamp)).getTime() -
      new Date(Date.parse(b.timestamp)).getTime()
  );
  const smsOrMailChecked =
    smsMailChoosen === "sms" || smsMailChoosen === "mail";
  const messageOrTemplate =
    (isStringNullOrEmpty(template) && !isStringNullOrEmpty(message)) ||
    !isStringNullOrEmpty(template);

  const canComplete =
    smsOrMailChecked && !isStringNullOrEmpty(to) && messageOrTemplate
      ? true
      : null;

  const handleCalling = async (number: string) => {
    try {
      await callApplicant({ to: number, country: Country.FI });
      flow.trigger({ to: number, isMail: false, isSms: false, isCall: true });
    } catch (err) {
      setError({
        error: err,
        message:
          "Was not able to make the call. Make sure you are logged on ITX before trying again."
      });
      setIsDropdownVisible(false);
    }
  };

  const callButtonClicked = async (): Promise<void> => {
    //If we don't have a coapplicant, we can just call the applicant
    if (applicants.length === 1) {
      await handleCalling(applicant?.mobileNumber);
    } else {
      //If we have a coapplicant, we need to show a dropdown with the applicant and coapplicant
      setIsDropdownVisible(!isDropdownVisible);
    }
  };

  const renderDropdown = () => {
    return (
      <List>
        {applicants.map((listedApplicant, index) => {
          return (
            <ListItem
              key={`Applicants-${index}`}
              onClick={() => handleCalling(listedApplicant?.mobileNumber)}
            >
              {listedApplicant?.firstName} {listedApplicant?.lastName}
            </ListItem>
          );
        })}
      </List>
    );
  };

  const flowTrigger = () => {
    // Check if both smsChecked and mailChecked are false
    if (smsMailChoosen !== "sms" && smsMailChoosen !== "mail") {
      return null;
    }

    let templateMessage: string | null = null;
    // Check if template is selected
    if (smsOrMailChecked && !isStringNullOrEmpty(template)) {
      // Get template from communicationTemplates
      const templateObject = emailTemplates[template];
      templateMessage = replaceVariablesInTemplateSE(
        message,
        templateObject.variables,
        data
      );
    }

    // Create options object with message, isMail, isSms, and isCall properties
    const options = {
      message: templateMessage ?? message,
      to,
      isMail: smsMailChoosen === "mail",
      isSms: smsMailChoosen === "sms",
      isCall: false
    };

    return flow.trigger(options);
  };

  const handleTemplateChange = (value: string) => {
    setTemplate(value);
    setMessage(emailTemplates[value].text);
  };

  const handleSmsMailChoosen = (value: string) => {
    if (
      value === "sms" &&
      !applicant?.mobileNumber &&
      !coapplicant?.mobileNumber
    ) {
      setSmsMailChoosen("");
      setError({
        error: "No mobile number found.",
        message: "No mobile number found. Try sending an email instead."
      });
    } else {
      setSmsMailChoosen(value as SmsOrMail);
      setTemplate("");
      setMessage("");
      setError(null);
    }
  };

  const communicationContent = () => {
    return (
      <>
        <SMSConversationWrapper>
          {smsAndMailSent?.map((message, index) => {
            return renderMessage(message, index);
          })}
        </SMSConversationWrapper>
        <DecisionSectionWrapper>
          <GapContainer style={{ marginTop: "20px" }}>
            <ApplicantInfo applicant={applicant} />
            {coapplicant && (
              <ApplicantInfo
                applicant={{ ...coapplicant, isCoApplicant: true }}
              />
            )}
          </GapContainer>

          <CallSectionWrapper>
            <SecondaryButton
              style={{
                display: "flex",
                alignItems: "center",
                overflow: "hidden"
              }}
              disabled={!canAct}
              onClick={callButtonClicked}
            >
              Click to call{" "}
              <IconWrapper>
                <Icons.Phone style={{ transform: "rotate(-125deg)" }} />
              </IconWrapper>
            </SecondaryButton>
            {isDropdownVisible ? renderDropdown() : null}
          </CallSectionWrapper>
          {UsertaskSelect(
            "Send SMS or Mail",
            smsMailOptions,
            smsMailChoosen,
            handleSmsMailChoosen
          )}
          {smsOrMailChecked
            ? smsMailChoosen === "sms"
              ? UsertaskSelect("To", smsToOptions, to, setTo)
              : UsertaskSelect("To", emailToOptions, to, setTo)
            : null}
          {smsOrMailChecked
            ? UsertaskSelect(
                "Template",
                templateOptions,
                template,
                handleTemplateChange,
                { marginTop: "10px" }
              )
            : null}
          {smsOrMailChecked
            ? UsertaskTextArea(message, setMessage, "Message")
            : null}
          {UsertaskButtons(canComplete, flowTrigger, () =>
            flow.save({
              smsTo: to,
              template: template,
              message: message,
              mailAndOrSms: mailAndOrSms
            })
          )}
          {!error ? null : (
            <StyledDecisionSectionWrapper>
              <StyledSpan>{error.message}</StyledSpan>
            </StyledDecisionSectionWrapper>
          )}
        </DecisionSectionWrapper>
      </>
    );
  };

  return CommonUserTask(communicationContent());
};

export default ContactApplicantFinland;
